// Aranabilir tek seçimli seçici — uzun listeler için (gösterge kodları, kategori ağacı).
// Tetikleyici düğme seçili öğeyi (etiket · kod · ipucu) gösterir; açılınca arama kutusu +
// listbox (ARIA combobox: ↑↓ / Enter / Esc / Home / End, aria-activedescendant), dışarı
// tıklayınca kapanır. Liste yerel ve senkron (sunucu araması için ClientPicker kullanılır).
// Yerel <select>'in yerine geçer; mobilde de aynı davranış (44 px dokunma hedefi).

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { groupRuns, orderByGroup, type SearchSelectOption } from "../../lib/search-select";
import { listKeyAction, moreText as defaultMoreText, searchSelectCore } from "./search-select-core";
import {
  codeStyle,
  groupHeaderStyle,
  hintStyle,
  itemStyle,
  listStyle,
  popStyle,
  rowTextStyle,
  searchStyle,
  triggerStyle,
} from "./search-select-styles";

export type { SearchSelectOption } from "../../lib/search-select";

export interface SearchSelectProps {
  id?: string | undefined;
  ariaLabel?: string | undefined;
  value: string;
  options: readonly SearchSelectOption[];
  onChange: (value: string) => void;
  /** boş seçim satırı ("— Kök —"); verilirse value "" seçilebilir */
  emptyLabel?: string | undefined;
  placeholder?: string | undefined;
  searchPlaceholder?: string | undefined;
  disabled?: boolean | undefined;
  size?: "sm" | "md" | undefined;
  /** tetikleyicide kodu da göster (varsayılan true) */
  showCode?: boolean | undefined;
  /** listelenecek en fazla sonuç (varsayılan 200); aşılırsa sonda `moreText` satırı */
  limit?: number | undefined;
  moreText?: ((shown: number, total: number) => string) | undefined;
  /** açık başla (SSR testleri — liste markup'ı) */
  initialOpen?: boolean | undefined;
}

export function SearchSelect(props: SearchSelectProps): JSX.Element {
  const {
    id,
    ariaLabel,
    value,
    options,
    onChange,
    emptyLabel,
    placeholder = "Seçin…",
    searchPlaceholder = "Ara…",
    disabled,
    size = "md",
    showCode = true,
    limit = 200,
    moreText,
  } = props;
  const genId = useId();
  const listId = `${genId}-list`;
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(props.initialOpen ?? false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  const all = useMemo<SearchSelectOption[]>(
    () =>
      emptyLabel !== undefined ? [{ value: "", label: emptyLabel }, ...options] : [...options],
    [emptyLabel, options],
  );
  const core = useMemo(
    () =>
      searchSelectCore({
        items: all,
        query,
        keyOf: (o) => o.value,
        labelOf: (o) => o.label,
        codeOf: (o) => o.code,
        hintOf: (o) => o.hint,
        max: limit,
      }),
    [query, all, limit],
  );
  const rows = useMemo(() => orderByGroup(core.visible), [core.visible]);
  const grouped = useMemo(() => all.some((o) => o.group !== undefined), [all]);
  const selected = all.find((o) => o.value === value) ?? null;

  useEffect(() => {
    if (!open) return;
    setQuery("");
    const idx = Math.max(
      0,
      all.findIndex((o) => o.value === value),
    );
    setActive(idx);
    requestAnimationFrame(() => inputRef.current?.focus());
    function onDoc(e: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, all, value]);

  useEffect(() => {
    if (!open) return;
    const el = document.getElementById(`${listId}-${active}`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active, open, listId]);

  function pick(o: SearchSelectOption): void {
    if (o.disabled) return;
    onChange(o.value);
    setOpen(false);
  }

  function renderOption(o: SearchSelectOption, i: number): JSX.Element {
    return (
      <li
        key={o.value || "__empty"}
        id={`${listId}-${i}`}
        role="option"
        aria-selected={o.value === value}
        aria-disabled={o.disabled ? true : undefined}
        title={o.title}
        onMouseEnter={() => setActive(i)}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => pick(o)}
        style={{
          ...itemStyle,
          paddingLeft: 10 + (o.depth ?? 0) * 14,
          opacity: o.disabled ? 0.5 : 1,
          background:
            i === active
              ? "color-mix(in srgb, var(--color-primary) 10%, var(--color-surface))"
              : "transparent",
          fontWeight: o.value === value ? 700 : 400,
        }}
      >
        <span style={rowTextStyle}>
          <span>{o.label}</span>
          {o.code ? <span style={codeStyle}>{o.code}</span> : null}
          {o.hint ? <span style={hintStyle}>{o.hint}</span> : null}
        </span>
      </li>
    );
  }

  const h = size === "sm" ? 32 : 40;
  const activeId = open && rows[active] ? `${listId}-${active}` : undefined;

  return (
    <div ref={rootRef} style={{ position: "relative", minWidth: 0 }}>
      <button
        type="button"
        id={id}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        style={{ ...triggerStyle, minHeight: h, fontSize: size === "sm" ? "0.8rem" : "0.875rem" }}
      >
        {selected ? (
          <span style={rowTextStyle}>
            <span style={{ fontWeight: selected.value === "" ? 400 : 600 }}>{selected.label}</span>
            {showCode && selected.code ? <span style={codeStyle}>{selected.code}</span> : null}
            {selected.hint ? <span style={hintStyle}>{selected.hint}</span> : null}
          </span>
        ) : (
          <span style={{ color: "var(--color-text-3)" }}>{placeholder}</span>
        )}
        <span aria-hidden="true" style={{ color: "var(--color-text-3)", marginLeft: "auto" }}>
          ▾
        </span>
      </button>
      {open ? (
        <div style={popStyle}>
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            {...(activeId ? { "aria-activedescendant": activeId } : {})}
            aria-label={`${ariaLabel ?? "Seçenek"} ara`}
            placeholder={searchPlaceholder}
            value={query}
            autoComplete="off"
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              const act = listKeyAction(e.key, active, rows.length);
              if (typeof act === "number") {
                e.preventDefault();
                setActive(act);
              } else if (act === "select") {
                e.preventDefault();
                if (rows[active]) pick(rows[active]);
              } else if (act === "close") {
                if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                }
                setOpen(false);
              }
            }}
            style={searchStyle}
          />
          <ul id={listId} role="listbox" aria-label={ariaLabel} style={listStyle}>
            {rows.length === 0 ? (
              <li style={{ ...itemStyle, color: "var(--color-text-3)", cursor: "default" }}>
                Eşleşen seçenek yok
              </li>
            ) : null}
            {grouped
              ? groupRuns(rows).map((run, r) =>
                  run.group === undefined ? (
                    run.items.map(({ option, index }) => renderOption(option, index))
                  ) : (
                    <li key={`__group-${r.toString()}`} role="group" aria-label={run.group}>
                      <div aria-hidden="true" style={groupHeaderStyle}>
                        {run.group}
                      </div>
                      <ul role="none" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                        {run.items.map(({ option, index }) => renderOption(option, index))}
                      </ul>
                    </li>
                  ),
                )
              : rows.map((o, i) => renderOption(o, i))}
            {core.more > 0 ? (
              <li
                role="presentation"
                style={{ ...itemStyle, color: "var(--color-text-3)", cursor: "default" }}
              >
                {moreText
                  ? moreText(rows.length, rows.length + core.more)
                  : defaultMoreText(core.more)}
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
